import { BaseFilterButton } from 'components/base-table/filters';
import { Button, ChakraSelect, FormControl, FormLabel, HStack, Stack, useBoolean, useDisclosure } from 'design-system';
import { usePageParams } from 'hooks/use-page-params';
import { statusObj } from 'pages/orders/utils/order-status';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TOption = {
  value: string;
  label: string;
};

type TInputs = {
  status: TOption | undefined;
};

const statusOptions: TOption[] = [
  {
    value: statusObj['approved']?.slug,
    label: statusObj['approved']?.name,
  },
  // No Draft: the CMS orders list never lists drafts (price requests, not orders).
  {
    value: statusObj['canceled']?.slug,
    label: 'Unsuccessful',
  },
  {
    value: statusObj['restaurant_placed']?.slug,
    label: statusObj['restaurant_placed']?.name,
  },
  {
    value: statusObj['supplier_placed']?.slug,
    label: statusObj['supplier_placed']?.name,
  },
  {
    value: statusObj['rejected']?.slug,
    label: 'Int. Canceled',
  },
  {
    value: statusObj['received']?.slug,
    label: statusObj['received']?.name,
  },
  {
    value: statusObj['restaurant_placed_approval']?.slug,
    label: statusObj['restaurant_placed_approval']?.name,
  },
];

const defaultValues: TInputs = {
  status: undefined,
};

export const OrderStatusFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  // Update active filter button
  useEffect(() => {
    if (params.status) setIsActive.on();
    else setIsActive.off();
  }, [params.status]);

  const { control, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Update values
  const updateValues = () => {
    setValue(
      'status',
      statusOptions.find((o) => {
        if (params?.status) {
          return o.value === params?.status;
        }

        return undefined;
      })
    );
  };

  useEffect(() => {
    updateValues();
  }, [params.status]);

  const onSubmit = (data: TInputs) => {
    params.status = data.status?.value as string;

    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    updateValues();
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='status'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>Status</FormLabel>
                <ChakraSelect
                  id='status'
                  options={statusOptions}
                  onChange={onChange}
                  name={name}
                  onBlur={onBlur}
                  value={value}
                />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
